import { useAuth } from '@/composables/useAuth';
import { useOnboardingDraft } from '@/composables/useOnboardingDraft';
import { useOnboardingSession } from '@/composables/useOnboardingSession';
import { getOnboardingRouteName } from '@/services/onboarding-progress.service';
import { getAvailableTariffs } from '@/services/onboarding.service';

export const RegistrationErrorKind = Object.freeze({
  INVALID_PROOF: 'invalidProof',
  ATTEMPTS_EXCEEDED: 'attemptsExceeded',
  ALREADY_COMPLETED: 'alreadyCompleted',
  CREDENTIALS_MISMATCH: 'credentialsMismatch',
  IN_PROGRESS: 'inProgress',
  TRANSIENT: 'transient',
  UNKNOWN: 'unknown',
});

// Как UI реагирует на ответ `POST /registrations/start` (контракт backend 0004):
// - 400 EMAIL_VERIFICATION_INVALID — proof использован/просрочен, нужен новый код;
// - 429 — исчерпан лимит попыток start по одному подтверждению, нужен новый код;
// - 409 EMAIL_VERIFICATION_IN_PROGRESS и 5xx/сеть — повтор с тем же proof безопасен;
// - 401 — пароль не подходит к существующему email: proof сохраняется, пароль можно поменять;
// - 409 ONBOARDING_ALREADY_COMPLETED — регистрация завершена, нужен вход.
export function classifyRegistrationError(error) {
  if (error?.code === 'EMAIL_VERIFICATION_INVALID' || error?.status === 400) {
    return RegistrationErrorKind.INVALID_PROOF;
  }
  if (error?.status === 429) return RegistrationErrorKind.ATTEMPTS_EXCEEDED;
  if (error?.code === 'ONBOARDING_ALREADY_COMPLETED') return RegistrationErrorKind.ALREADY_COMPLETED;
  if (error?.status === 409) return RegistrationErrorKind.IN_PROGRESS;
  if (error?.status === 401) return RegistrationErrorKind.CREDENTIALS_MISMATCH;
  if (!error?.status || error.status >= 500) return RegistrationErrorKind.TRANSIENT;
  return RegistrationErrorKind.UNKNOWN;
}

export function isRetryableRegistrationError(kind) {
  return kind === RegistrationErrorKind.TRANSIENT || kind === RegistrationErrorKind.IN_PROGRESS;
}

// Эти исходы делают proof непригодным: подтверждение email нужно пройти заново.
export function isProofLostRegistrationError(kind) {
  return kind === RegistrationErrorKind.INVALID_PROOF || kind === RegistrationErrorKind.ATTEMPTS_EXCEEDED;
}

// Общая точка входа в onboarding для `registrations/start` и `registrations/resume`:
// поднимает onboarding-сессию, auth-сессию без организации и восстанавливает черновик
// (только allowlist-поля, см. `sanitizeOnboardingDraft`). Возвращает имя маршрута шага.
export async function activateOnboardingRegistration(registration, fallbackRegion = '') {
  const { acceptOnboardingAuth } = useAuth();
  const { acceptRegistration } = useOnboardingSession();
  const {
    resetDraft,
    restoreDraft,
    setAvailableTariffs,
    updateAccount,
  } = useOnboardingDraft();

  acceptOnboardingAuth(registration);
  acceptRegistration(registration, registration.region || fallbackRegion);

  if (registration.draft && Object.keys(registration.draft).length) {
    restoreDraft(registration.draft);
  } else {
    resetDraft();
  }
  updateAccount({
    email: registration.email,
    phone: registration.phone,
    personalDataAccepted: registration.personalDataAccepted,
  });

  try {
    setAvailableTariffs(await getAvailableTariffs());
  } catch {
    // Аккаунт уже создан: каталог тарифов повторно загрузит шаг «Выбор тарифа».
  }

  return getOnboardingRouteName(registration.currentStep);
}
